import { ChangeEvent, useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { emailIsValid } from '@/components/signup/utils/email-is-valid';
import { fontSize } from '@/components/signup/utils/font-size';
import wacToken from '@/libraries/design/foundations/token/src/wac-token';
import { useDarkTheme } from '@/libraries/design/utils/dark-theme-provider';

import { useAccountCreationContext } from '../../hooks/account-creation-context';
import { InputIcon } from '../input-icon/input-icon';

const HELP_TEXT_ID = 'account_creation_email_help_text';

export const Input = ({
	helpText,
	errorMessageText,
	labelText,
	placeholderText,
}: {
	helpText?: string | null;
	errorMessageText?: string;
	labelText?: string;
	placeholderText?: string;
}) => {
	const {
		isReadyForInvalid,
		setIsEmailValid,
		setIsReadyForInvalid,
		setTypeAheadEmail,
		typeAheadEmail,
	} = useAccountCreationContext();
	const [email, setEmail] = useState(typeAheadEmail);
	const [isValid, setValid] = useState<boolean>();
	const intl = useIntl();
	const { isDarkTheme } = useDarkTheme();

	if (helpText === undefined) {
		helpText = intl.formatMessage({
			id: 'signupForm.accountCreation.helperText',
			defaultMessage: 'Using a work email helps find teammates and boost collaboration.',
		});
	}
	if (errorMessageText === undefined) {
		errorMessageText = intl.formatMessage({
			id: 'BxpUiSignup.SignupForm.invalidEmail',
			defaultMessage: 'Please enter a valid email address.',
		});
	}
	if (labelText === undefined) {
		labelText = intl.formatMessage({
			id: 'BxpUiSignup.SignupForm.workEmail',
			defaultMessage: 'Work email',
		});
	}
	if (placeholderText === undefined) {
		placeholderText = intl.formatMessage({
			id: 'BxpUiSignup.SignupForm.emailPlaceholder',
			defaultMessage: 'you@company.com',
		});
	}

	useEffect(() => {
		const isValid = emailIsValid(email);

		// wait to show invalid state if the user is not finished typing
		if (!isValid && !isReadyForInvalid) {
			setValid(undefined);
			return;
		}

		setValid(isValid);
		setIsEmailValid(isValid);
	}, [email, setIsEmailValid, isReadyForInvalid]);

	return (
		<div css={[fieldCss, isDarkTheme && darkThemeFieldCss]}>
			<Field aria-required={true} isRequired label={labelText} name="email">
				{({ fieldProps }) => (
					<div
						css={[
							inputCss,
							isValid === true && inputValidCss,
							isValid === false && inputInvalidCss,
						]}
					>
						<TextField
							{...fieldProps}
							autoComplete="email"
							aria-describedby={HELP_TEXT_ID}
							elemAfterInput={<InputIcon valid={isValid} />}
							onBlur={() => setIsReadyForInvalid(true)}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								setEmail(e.target.value);
								setTypeAheadEmail(e.target.value);
								fieldProps.onChange(e);
							}}
							placeholder={placeholderText}
							testId="email_field"
							type="email"
							value={email}
						/>
						{isValid === false ? (
							<div
								css={[errorMessageCss, isDarkTheme && darkThemeErrorMessageCss]}
								id={HELP_TEXT_ID}
								role="alert"
							>
								{errorMessageText}
							</div>
						) : helpText ? (
							<div css={helpTextCss} id={HELP_TEXT_ID}>
								{helpText}
							</div>
						) : null}
					</div>
				)}
			</Field>
		</div>
	);
};

const errorMessageCss = css({
	color: token('color.text.danger'),
	fontSize: fontSize.xs,
	lineHeight: 1.5,
	marginBlockStart: 6,
});

const darkThemeErrorMessageCss = css({
	// color value copied from ADS form error in dark theme
	// eslint-disable-next-line local/ensure-wac-token-usage
	color: '#FD9891',
});

const helpTextCss = css({
	fontSize: fontSize.xs,
	lineHeight: 1.5,
	color: 'var(--wac-color-body)',
	marginBlockStart: 6,
});

const fieldCss = css({
	marginBlockEnd: 16,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	input: {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		fontFamily: 'inherit',
		fontSize: fontSize.md,
		paddingInlineStart: '12px !important',
	},
	['&:first-of-type']: {
		marginBlockStart: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	label: {
		color: 'inherit',
		fontFamily: 'inherit',
		fontWeight: 500,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- hide asterisk
		['[aria-hidden=true]']: {
			display: 'none',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderWidth: token('border.width', '1px'),
		paddingBlock: 1,
		['&:focus-within']: {
			borderWidth: token('border.width.focused', '2px'),
			paddingBlock: 0,
		},
	},
});

const darkThemeFieldCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['[data-ds--text-field--container]']: {
		backgroundColor: `${wacToken('color.neutral.dark200')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	input: {
		backgroundColor: `${wacToken('color.neutral.dark200')} !important`,
		color: wacToken('color.neutral.light0'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	label: {
		color: wacToken('color.neutral.light0'),
	},
});

const inputCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderRadius: 21,
	},
});

const inputInvalidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderColor: `${token('color.border.danger')} !important`,
		borderWidth: token('border.width.focused', '2px'),
		paddingBlock: 0,
	},
});

const inputValidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderColor: `${token('color.border.success')} !important`,
		borderWidth: token('border.width.focused', '2px'),
		paddingBlock: 0,
	},
});
